import { useForm } from 'antd/lib/form/Form';
import React, { ChangeEvent, useEffect } from 'react';
import { Button, Form, Input, ModalFuncProps, Select, Space } from 'antd';
import { useContextUsers } from '../../../../../context/users';
import { useAuth } from '../../../../../context/auth';
import { camelize } from '../../../../../utils/text';
import { CLOSING_COST_TYPES } from '../../../Reports/constants';
import { EnumClosingCostType } from '../../../../../types/settings';

interface IClosingCostFormProps extends ModalFuncProps {
  closingCostId?: number | undefined;
}

const ClosingCostForm: React.FC<IClosingCostFormProps> = ({ open, onCancel, closingCostId }) => {
  const [form] = useForm()
  const { user: { id: userId } } = useAuth()

  const {
    closingCostSettings,
    createClosingCostSettings,
    updateClosingCostSettings,
    getClosingCostSettings
  } = useContextUsers()

  useEffect(() => {
    if (!open) return
    form.resetFields()
  }, [open])

  useEffect(() => {
    if (!closingCostId) {
      form.resetFields();

      return
    };

    const fetchClosingCost = async () => {
      await getClosingCostSettings?.(userId)
      const closingCostSetting = closingCostSettings?.find((item) => item.id === closingCostId)

      if (closingCostSetting) {
        form.setFieldsValue({
          name: closingCostSetting.text,
          type: closingCostSetting.type,
          amount: Number(closingCostSetting.amount),
          propertyTaxesMonthsCollected: closingCostSetting.propertyTaxesMonthsCollected,
          daysCollected: closingCostSetting.daysCollected,
        });
      }
    };

    fetchClosingCost();

    // eslint-disable-next-line consistent-return
    return () => {
      form.resetFields();
    }
  }, [closingCostId, userId]);


  const handleSubmit = async (values: {
    name: string;
    type: string;
    amount: number;
    propertyTaxesMonthsCollected?: number;
    daysCollected?: number;
  }) => {
    if (createClosingCostSettings && getClosingCostSettings) {
      const isUpdating = !!closingCostId;
      const closingCostData = {
        name: camelize(values.name),
        type: values.type,
        text: values.name || '',
        order: 1,
        active: 1,
        userId,
        id: closingCostId,
        amount: values.amount,
        ...(values?.propertyTaxesMonthsCollected &&
          { propertyTaxesMonthsCollected: values.propertyTaxesMonthsCollected }),
        ...(values?.daysCollected &&
          { daysCollected: values.daysCollected })
      };

      if (isUpdating) {
        await updateClosingCostSettings?.(closingCostData, userId);
      } else {
        await createClosingCostSettings(closingCostData);
      }

      getClosingCostSettings(userId);
      onCancel?.();
    }
  }

  return (
    <Form form={form} layout='vertical' onFinish={handleSubmit}>
      <Form.Item
        name='name'
        label='Name'
        rules={[
          { required: true, message: 'Please enter the name' },
          { min: 3, message: 'Name must be at least 3 characters' },
          { max: 50, message: 'Name cannot be longer than 50 characters' },
        ]}
      >
        <Input size='large' placeholder='Name' />
      </Form.Item>
      <Form.Item
        name='type'
        label='Type'
        rules={[{ required: true, message: 'Please select the type' }]}
      >
        <Select size='large' placeholder='Select Type'>
          {CLOSING_COST_TYPES.map((type) => (
            <Select.Option key={type.value} value={type.value}>
              {type.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}>
        {({ getFieldValue }) => {
          const type = getFieldValue('type');
          const prefix =
            // eslint-disable-next-line no-nested-ternary
            (
              type === EnumClosingCostType.flatFee ||
              type === EnumClosingCostType.propertyTaxes ||
              type === EnumClosingCostType.homeownersInsurance
            )
              ? '$'
              : (type === EnumClosingCostType.percentageOfLoanAmount
                || type === EnumClosingCostType.percentageOfPurchasePrice)
                ? '%'
                : '';
          const isPrepaidInterest = type === EnumClosingCostType.prepaidInterest15Days;
          const isPropertyTaxes = type === EnumClosingCostType.propertyTaxes
          const isHomeownersInsurance = type === EnumClosingCostType.homeownersInsurance

          const isPrefixDollarSign = prefix?.includes('$');

          const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
            const { value } = event.target;

            if (isPrefixDollarSign) {
              form.setFieldValue('amount', value.replace(/[^0-9.-]/g, ''))
            } else {
              form.setFieldValue('amount', value.replace(/[^0-9.-]/g, ''))
            }
          }

          return (
            <>
              {(isPropertyTaxes || isHomeownersInsurance) && (
                <Form.Item
                  name='propertyTaxesMonthsCollected'
                  label='Months Collected'
                  rules={[{ required: true, message: 'Please select number of months collected' }]}
                  initialValue={isPropertyTaxes ? 3 : 2}
                >
                  <Select placeholder="Select months collected">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <Select.Option key={month} value={month}>
                        {month}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              {isPrepaidInterest && (
                <Form.Item
                  name='daysCollected'
                  label='Days Collected'
                  rules={[{ required: true, message: 'Please select number of days collected' }]}
                  initialValue={15}
                >
                  <Select placeholder="Select days collected">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <Select.Option key={day} value={day}>
                        {day}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              {!isPropertyTaxes && !isHomeownersInsurance && !isPrepaidInterest && (
                <Form.Item
                  name="amount"
                  label="Amount"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter the amount',
                    },
                    {
                      type: 'number',
                      message: 'Amount must be a numeric value',
                      transform: (value) => Number(value),
                    },
                  ]}
                >
                  <Input
                    name="amount"
                    placeholder="Enter Amount"
                    prefix={prefix}
                    defaultValue={0}
                    onChange={handleAmountChange}
                  />
                </Form.Item>
              )}
            </>
          );
        }}
      </Form.Item>

      <Space>
        <Button type='primary' size='large' htmlType='submit'>Save</Button>
        <Button size='large' onClick={() => onCancel?.()}>Cancel</Button>
      </Space>
    </Form>
  )
}

ClosingCostForm.defaultProps = {
  closingCostId: undefined,
}

export default ClosingCostForm
